import { decode as msgPackDecode } from '@msgpack/msgpack/dist/decode';
import { isArray, isEmpty, isString } from 'lodash';
import { Observable } from 'rxjs';

import configLib from '../../../configLib';
import { PredefinedMilisecondsTypeEnum } from '../../enums';
import { sleep } from '../../util/CommonHelpers';
import { lz4Unpickle } from '../../util/GatewayHelpers';
import { uuidv4 } from '../../util/tools';
import { NullableString, NullableTimeout } from '../../util/types';
import { CustomEventService } from '../custom-event/CustomEvent.service';
import { WebSocketEventData, WebSocketEventTypes } from '../custom-event/types';
import { LoggerService } from '../logger/Logger.service';
import { BaseSubscriptionRequestType } from '../subscription/types';

import { WEB_SOCKET_CONNECTION_TRY_COUNT, WEB_SOCKET_MAX_SUCCESS_STATUS } from './constants';
import { GatewayRequestType, GatewayResponseType } from './types';

const { WebSocket: WebSocketImpl, Response } = configLib;

const LOG_PREFIX = '[BASE-WS]';
const logger = LoggerService.getLogger();

export abstract class BaseWebSocketService<T extends BaseSubscriptionRequestType> {
  private eventService: CustomEventService<any> = new CustomEventService<WebSocketEventData<any>>();

  private webSocket?: WebSocket | null;

  private token: NullableString;

  private loginTimeout: PredefinedMilisecondsTypeEnum;

  private loginTimeoutId: NullableTimeout;

  private pingIntervalId: NullableTimeout;

  private isLoggedin = false;

  private connectionTryCount = 0;

  private requestQueue: T[] = [];

  constructor(token: NullableString, loginTimeout: PredefinedMilisecondsTypeEnum) {
    this.token = token;
    this.loginTimeout = loginTimeout;
  }

  public async connect(tryCount = WEB_SOCKET_CONNECTION_TRY_COUNT) {
    if (this.connectionTryCount >= tryCount) {
      logger.warn(`${LOG_PREFIX} max reconnection count exceeded! Please check your connection:`, this.getWebSocketState());
      return;
    }

    if (this.connectionTryCount > 0) { // if it's not the first time connection try, wait and try
      await sleep(PredefinedMilisecondsTypeEnum.oneSecond);
    }

    this.connectionTryCount++;
    if (this.isConnected() && this.isAuthenticated()) {
      logger.warn(
        `${LOG_PREFIX} Reconnect will be canceled! it's already been connected and authenticated. state:`,
        this.getWebSocketState(),
      );
      return;
    }

    if (this.isBusy()) {
      logger.warn(`${LOG_PREFIX} Connection is CLOSING/OPENING! Please try again to connect later.state:`, this.getWebSocketState());
      return;
    }

    this.webSocket = new WebSocketImpl(this.getBaseUrl());
    this.checkLoginStatus();
    this.checkServerByPing();

        // TODO: Cover this with integration test ART-4281
        this.webSocket!.onopen = (): void => {
          this.resetTryCountToDefault();
          this.makeLoginRequest();
        };

        // TODO: Cover this with integration test ART-4281
        this.webSocket!.onmessage = (response: any) => {
          try {
            this.handleGWWebsocketMessages(response);
          } catch (error) {
            logger.error(`${LOG_PREFIX} Can not handle WebSocket response! error:`, error);
            this.eventService.publishEvent({
              data: response,
              eventType: WebSocketEventTypes.UNKNOWN_RESPONSE,
            });
          }
        };

        // TODO: Cover this with integration test ART-4281
        this.webSocket!.onclose = (event: any): void => {
          logger.warn(`${LOG_PREFIX} WebSocket connection closed!`);
          this.webSocket = null; // Give an opportunity to Garbage Collector to remove this instance
          this.isLoggedin = false;
          this.eventService.publishEvent({
            eventType: WebSocketEventTypes.CLOSED,
          });
        };

        // TODO: Cover this with integration test ART-4281
        this.webSocket!.onerror = (event: any) => {
          logger.error(`${LOG_PREFIX} An error occurred on WebSocket connection create!, event:`, event);
          this.isLoggedin = false;
          this.eventService.publishEvent({
            data: event,
            eventType: WebSocketEventTypes.ERROR,
          });

          this.connect();
        };
  }

  public resetTryCountToDefault(): void {
    this.connectionTryCount = 0;
  }

  public clearRequestQueue(): void {
    this.requestQueue = [];
  }

  public addEventListener(): Observable<WebSocketEventData<any>> {
    return this.eventService.getEventAsObservable();
  }

  async handleGWWebsocketMessages(response: any): Promise<void> {
    let { data: dataRaw } = response;
    const isStringResponse = isString(dataRaw);

    if (!isStringResponse && configLib.isWeb) {
      dataRaw = await new Response(dataRaw).arrayBuffer();// Just in case, this logic was moved from the old implementation
    }

    const responseData = this.getResponseData(isStringResponse, dataRaw);

    const { messageType, resultCode, httpCode } = responseData;
    const isSuccess = this.isSuccessResponse(resultCode, httpCode);

    if (isArray(responseData)) {
      this.eventService.publishEvent({
        data: {
          arrayData: responseData,
          messageType: responseData[0],
          messageSubType: responseData[1][0],
        },
        eventType: WebSocketEventTypes.PARSED_RESPONSE,
        key: uuidv4(),
      });

      return;
    }

    if (isStringResponse && messageType === GatewayResponseType.Login) {
      if (isSuccess) {
        this.isLoggedin = true;
        this.sendAllQueuedRequest();
      } else {
        this.disconnect();
      }
    }

    this.eventService.publishEvent({
      data: responseData,
      eventType: WebSocketEventTypes.PARSED_RESPONSE,
      key: responseData.traceIdentifier,
    });
  }

  public isConnected = (): boolean => this.webSocket?.readyState === WebSocketImpl.OPEN;

  public isClosed = (): boolean => this.webSocket?.readyState === WebSocketImpl.CLOSED;

  public isBusy = (): boolean => !!this.webSocket && !this.isConnected() && !this.isClosed();

  public isAuthenticated = (): boolean => this.isConnected() && this.isLoggedin;

  public disconnect(): void {
    logger.warn(`${LOG_PREFIX} Socket connection disconnecting... currentStatus:`, this.getWebSocketState());
    if (this.isConnected()) {
      this.isLoggedin = false;
      this.webSocket?.close();
      clearTimeout(this.loginTimeoutId as NodeJS.Timeout);
      clearInterval(this.pingIntervalId as NodeJS.Timeout);
    }
  }

  public setToken = (token: string): void => {
    this.token = token;
    this.makeLoginRequest();
  };

  public sendRequest(requestBody: T): void {
    if (!this.isConnected()) {
      logger.warn(`${LOG_PREFIX} Message can not be sent, no WS connection! Data:`, requestBody);
      this.requestQueue.push(requestBody);
      this.connect();
      return;
    }

    if (requestBody.messageType !== GatewayRequestType.Login && !this.isAuthenticated()) {
      logger.warn(`${LOG_PREFIX} Message can not be sent, no authentication! Data:`, requestBody);
      this.requestQueue.push(requestBody);
      this.connect();

      return;
    }

    this.sendAsString(requestBody);
  }

  private sendAsString(requestBody: T): void {
    this.webSocket?.send(JSON.stringify(requestBody));
  }

    protected abstract getBaseUrl(): string;

    protected abstract cacheResponse(): string;

    private isSuccessResponse(resultCode: any, httpCode: any) {
      const statusCode = resultCode || httpCode;
      return statusCode <= WEB_SOCKET_MAX_SUCCESS_STATUS;
    }

    private getWebSocketState(): string {
      switch (this.webSocket?.readyState) {
        case WebSocketImpl.OPEN:
          return 'OPEN';
        case WebSocketImpl.CONNECTING:
          return 'CONNECTING';
        case WebSocketImpl.CLOSED:
          return 'CLOSED';
        case WebSocketImpl.CLOSING:
          return 'CLOSING';
        default:
          return 'N/A';
      }
    }

    private getResponseData(isStringResponse: boolean, dataRaw: any = {}) {
      let responseData: any = {};
      if (isStringResponse) {
        try {
          return JSON.parse(dataRaw) || {};
        } catch (error) {
          logger.error(`${LOG_PREFIX} Error parsing JSON message! error:${error}`);
          this.eventService.publishEvent({
            data: dataRaw,
            eventType: WebSocketEventTypes.UNKNOWN_RESPONSE,
          });
        }

        return responseData;
      }

      // if it is not a String response
      try {
        const uint8Array = new Uint8Array(dataRaw);
        const uncompressed = lz4Unpickle(uint8Array);
        return msgPackDecode(uncompressed) || {};
      } catch (error) {
        logger.error(`${LOG_PREFIX} decode response failed! error:`, error);
        responseData = dataRaw;
        this.eventService.publishEvent({
          data: responseData,
          eventType: WebSocketEventTypes.UNKNOWN_RESPONSE,
        });
      }

      return responseData;
    }

    /**
     * This is not related to WebSocket communication logic!
     * But the WebSocket depended on the Login.
     * That's why Login request was added into this class
     */
    private makeLoginRequest(): void {
      this.sendRequest({
        messageType: GatewayRequestType.Login,
        traceIdentifier: uuidv4(),
        token: this.token,
      } as T);
    }

    private pingRequest(): void {
      this.sendRequest({
        messageType: GatewayRequestType.Ping,
        traceIdentifier: uuidv4(),
      } as T);
    }

    private sendAllQueuedRequest() {
      if (isEmpty(this.requestQueue)) {
        // Do nothing because there is no queued request to send!
        return;
      }

      this.requestQueue.forEach((item, index) => {
        this.sendRequest(item);
        delete this.requestQueue[index];
      });
    }

    private checkServerByPing() {
      this.pingIntervalId = setInterval(() => {
        if (this.isLoggedin) {
          this.pingRequest();
        }
      }, PredefinedMilisecondsTypeEnum.oneMinute);
    }

    private checkLoginStatus() {
      // If there is no point within a given period, disconnect from the WS server!
      this.loginTimeoutId = setTimeout(() => {
        if (this.isConnected() && !this.isLoggedin) {
          // The connection opened but there is no login event!
          this.eventService.publishEvent({
            eventType: WebSocketEventTypes.LOGIN_TIMEOUT,
          });
          this.disconnect();
        }
      }, this.loginTimeout);
    }
}
